import { useViewNotificationMutation } from '@/app/api';
import { NotificationDataItem, NotificationReceiver } from '@/app/api/types';
import { Box, Typography, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface NotificationSidebarItemProps {
  data: NotificationDataItem;
  onClose: () => void;
  onOpenNewRequest: () => void;
}

const textSx = {
  fontSize: 10,
  fontWeight: 500,
  color: 'primary.main',
  cursor: 'pointer',
};

const generateLinkToDeal = (
  receiver: NotificationReceiver,
  deal: number
): string => {
  const { isBuyer, isSeller, isFactor, isCreator } = receiver.data;

  if (isCreator && (isBuyer || isSeller)) return `/seller/deals/${deal}`;
  if (isFactor) return `/factor/requests/${deal}`;

  return `/buyer/incoming-requests/${deal}`;
};

const NotificationSidebarItem: FC<NotificationSidebarItemProps> = ({
  data,
  onClose,
  onOpenNewRequest,
}) => {
  const { t } = useTranslation();
  const [viewNotification] = useViewNotificationMutation();

  const receiver = data.receiver?.[0];
  const isViewed = receiver.data.statusId === 'viewed';

  const handleWatchNotification = () => {
    viewNotification(data.unique);
    onClose();
  };

  return (
    <Box sx={{ py: 1, borderBottom: '1px solid #0B13701A' }}>
      <Typography
        sx={{ fontSize: 10, fontWeight: 500, color: '#0B1370', opacity: 0.5 }}
      >
        {dayjs(data.createdAt).format('HH:mm DD.MM.YYYY')}
      </Typography>

      <Stack direction='row' alignItems='center' spacing={1}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: '#0B1370',
            opacity: isViewed ? 0.5 : 1,
          }}
        >
          {data.notificationText}
        </Typography>

        {!isViewed && (
          <Box
            sx={{
              minWidth: 6,
              minHeight: 6,
              background: 'primary.main',
              borderRadius: '50%',
            }}
          />
        )}
      </Stack>

      <Stack direction='row' justifyContent='flex-end' spacing={1} mt={1}>
        {data.type === 'factorLimitEstablished' ? (
          <Typography sx={textSx} onClick={onOpenNewRequest}>
            {t('leave_request')}
          </Typography>
        ) : (
          <Link
            to={generateLinkToDeal(receiver, data.dealUnique)}
            onClick={handleWatchNotification}
          >
            <Typography sx={textSx}>{t('look')}</Typography>
          </Link>
        )}
      </Stack>
    </Box>
  );
};

export default NotificationSidebarItem;
